import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin, Plus, Save, X, CheckCircle2 } from "lucide-react";

export default function CrmZoneOperative() {
    const [zones, setZones] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/profile/zone-operative")
            .then((r) => setZones(r.data.zone_operative || []))
            .catch(() => setZones([]))
            .finally(() => setLoading(false));
    }, []);

    const add = () => {
        const v = input.trim();
        if (!v) return;
        if (zones.map((z) => z.toLowerCase()).includes(v.toLowerCase())) { setInput(""); return; }
        setZones([...zones, v]);
        setInput("");
        setSaved(false);
    };
    const remove = (idx) => { setZones(zones.filter((_, i) => i !== idx)); setSaved(false); };

    const save = async () => {
        setErr("");
        setSaving(true); setSaved(false);
        try {
            const response = await api.put("/profile/zone-operative", { zone_operative: zones });
            const persistedZones = response.data.zone_operative || [];
            setZones(persistedZones);
            window.dispatchEvent(new CustomEvent("vendidirect:zone-operative-updated", {
                detail: { zone_operative: persistedZones },
            }));
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore nel salvataggio");
        } finally { setSaving(false); }
    };

    if (loading) return <div className="text-ink-400 text-sm py-12 text-center">Caricamento…</div>;

    return (
        <div data-testid="crm-zone-operative" className="space-y-6 max-w-3xl">
            <div>
                <span className="label-eyebrow text-gold">VD Manager · Configurazione</span>
                <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-2">
                    <MapPin size={22} className="text-gold" /> Zone Operative
                </h2>
                <p className="text-ink-400 text-sm mt-1">Città e province in cui opera la tua agenzia. Riceverai automaticamente i lead di proprietari da queste zone tramite il form pubblico <span className="text-gold">/vendi-casa</span>. Puoi lasciare la lista vuota per sospendere il monitoraggio territoriale.</p>
            </div>

            <div className="border border-gold/30 bg-ink-900/40 p-8 space-y-4">
                <div className="flex flex-col sm:flex-row gap-2">
                    <input
                        data-testid="crm-zone-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                        placeholder="Es. Milano, Monza, Provincia di Bergamo..."
                        className="flex-1 bg-transparent border border-white/15 px-4 py-2.5 text-ink-50 focus:border-gold outline-none text-sm"
                    />
                    <button data-testid="crm-zone-add" type="button" onClick={add} className="cta-outline px-4 py-2.5 text-xs flex items-center gap-1.5 whitespace-nowrap">
                        <Plus size={12} /> Aggiungi
                    </button>
                </div>

                {zones.length > 0 ? (
                    <div data-testid="crm-zone-chips" className="flex flex-wrap gap-2">
                        {zones.map((z, i) => (
                            <span key={`${z}-${i}`} data-testid={`crm-zone-chip-${i}`} className="inline-flex items-center gap-1.5 border border-gold/40 bg-gold/5 text-gold text-xs px-3 py-1.5">
                                {z}
                                <button type="button" onClick={() => remove(i)} data-testid={`crm-zone-remove-${i}`} className="hover:text-ink-50"><X size={12} /></button>
                            </span>
                        ))}
                    </div>
                ) : (
                    <div className="text-xs text-amber-300 border border-amber-700/30 bg-amber-900/10 px-3 py-2">Nessuna zona configurata. Aggiungi almeno una città per ricevere lead.</div>
                )}

                {err && <div data-testid="crm-zone-error" className="text-xs text-red-300 border border-red-700/40 bg-red-900/20 px-3 py-2">{err}</div>}
                {saved && <div data-testid="crm-zone-saved" className="text-xs text-green-300 border border-green-700/40 bg-green-900/20 px-3 py-2 flex items-center gap-2"><CheckCircle2 size={13} /> Zone salvate correttamente</div>}

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || zones.length === 0}
                        data-testid="crm-zone-save"
                        className="cta-gold px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save size={14} /> {saving ? "Salvataggio…" : "Salva zone"}
                    </button>
                </div>
            </div>
        </div>
    );
}
